import { Cause, Effect, Exit, Fiber } from "effect"
import { useEffect, useState, type RefObject } from "react"
import { Gpu } from "./Gpu.ts"
import type { Scene } from "./Scene.ts"

export type SceneStatus = "idle" | "running" | "failed" | "stopped"

/**
 * Runs `scene` against the canvas in `ref` while `enabled` is true.
 *
 * The scene is forked as a scoped fiber with the `Gpu` layer provided; unmount
 * (or `enabled` flipping false) interrupts the fiber, which runs every
 * finalizer — frame loop, listeners, surface, and finally the GPU device.
 */
export function useGpuScene(
  ref: RefObject<HTMLCanvasElement | null>,
  scene: Scene,
  enabled: boolean,
): SceneStatus {
  const [status, setStatus] = useState<SceneStatus>("idle")

  useEffect(() => {
    const canvas = ref.current
    if (!enabled || canvas === null) {
      setStatus("idle")
      return
    }
    setStatus("running")

    const program = scene(canvas).pipe(
      Effect.scoped,
      Effect.provide(Gpu.layer),
      Effect.exit,
      Effect.map((exit) => {
        if (Exit.isFailure(exit) && !Cause.hasInterruptsOnly(exit.cause)) {
          console.error("[gpu] scene failed", Cause.pretty(exit.cause))
          setStatus("failed")
        }
      }),
    )
    const fiber = Effect.runFork(program)

    return () => {
      Effect.runFork(Fiber.interrupt(fiber))
      setStatus("stopped")
    }
  }, [ref, scene, enabled])

  return status
}
